import { useMemo } from "react";
import { Building2, Trophy, Users, Target } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtInt, fmtPct, pct } from "@/lib/format";

export default function ConsultorPanelDialog({ open, onOpenChange, consultor = "", condominios = [], clientes = [] }) {
  const meus = useMemo(
    () => condominios.filter((c) => (c.consultor || "").trim() === consultor && (c.infra_status || "atendido") === "atendido"),
    [condominios, consultor]
  );

  const totals = useMemo(() => {
    const vendas = meus.reduce((s, c) => s + Number(c.vendas_realizadas || 0), 0);
    const meta = meus.reduce((s, c) => s + Number(c.meta_vendas || 0), 0);
    const apt = meus.reduce((s, c) => s + Number(c.qtd_apartamentos || 0), 0);
    const clientesAtuais = meus.reduce((s, c) => s + Number(c.qtd_clientes_atuais || 0), 0);
    return { vendas, meta, apt, clientesAtuais, prédios: meus.length };
  }, [meus]);

  const clientesPorCondo = useMemo(() => {
    const map = {};
    for (const c of clientes) {
      const key = (c.condominio || "").trim();
      if (!key) continue;
      map[key] = (map[key] || 0) + 1;
    }
    return map;
  }, [clientes]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dialog-consultor-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8">
            <Trophy className="w-5 h-5 text-primary shrink-0" />
            <span className="truncate">Painel de {consultor || "consultor"}</span>
            <Badge variant="outline">{totals.prédios} prédio{totals.prédios !== 1 ? "s" : ""}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
          <div className="rounded-lg border border-border bg-background p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Vendas</div>
            <div className="font-heading text-xl font-bold tabular-nums text-primary">{fmtInt(totals.vendas)}{totals.meta > 0 && <span className="text-sm text-muted-foreground">/{fmtInt(totals.meta)}</span>}</div>
            {totals.meta > 0 && <div className="text-[10px] text-muted-foreground">{fmtPct(pct(totals.vendas, totals.meta))} da meta</div>}
          </div>
          <div className="rounded-lg border border-border bg-background p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Apartamentos/Salas</div>
            <div className="font-heading text-xl font-bold tabular-nums">{fmtInt(totals.apt)}</div>
          </div>
          <div className="rounded-lg border border-border bg-background p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Clientes atuais</div>
            <div className="font-heading text-xl font-bold tabular-nums text-emerald-500">{fmtInt(totals.clientesAtuais)}</div>
            {totals.apt > 0 && <div className="text-[10px] text-muted-foreground">{fmtPct(pct(totals.clientesAtuais, totals.apt))} de penetração</div>}
          </div>
          <div className="rounded-lg border border-border bg-background p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Prédios</div>
            <div className="font-heading text-xl font-bold tabular-nums">{fmtInt(totals.prédios)}</div>
          </div>
        </div>

        {meus.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center mt-4">
            Este consultor ainda não tem condomínios atendidos atribuídos.
          </div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden mt-4">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Condomínio</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Apt/Salas</TableHead>
                  <TableHead className="text-right">Vendas / Meta</TableHead>
                  <TableHead className="text-right">Clientes vinculados</TableHead>
                  <TableHead className="text-right">% atingido</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {meus.map((c) => {
                  const p = pct(c.vendas_realizadas, c.meta_vendas);
                  const tone = p >= 100 ? "text-emerald-500" : p >= 50 ? "text-amber-500" : "text-red-500";
                  return (
                    <TableRow key={c.id} data-testid={`consultor-condo-${c.id}`}>
                      <TableCell className="font-medium">{c.condominio}</TableCell>
                      <TableCell className="text-sm capitalize text-muted-foreground">{c.tipo}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtInt(c.qtd_apartamentos || 0)}</TableCell>
                      <TableCell className="text-right tabular-nums font-mono">
                        {fmtInt(c.vendas_realizadas || 0)} / {fmtInt(c.meta_vendas || 0)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{fmtInt(clientesPorCondo[c.condominio] || 0)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className={`font-mono tabular-nums font-semibold ${tone}`}>{fmtPct(p)}</span>
                          <div className="w-16"><Progress value={Math.min(100, Math.max(0, p))} className="h-1.5" /></div>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
